import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, ButtonBase } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import MediaThumbnail from './MediaThumbnail';
import useHoverIntent from '../../hooks/useHoverIntent';
import { getFileType } from '../../utils/commonServices';
import {
  MOBILE,
  ROW_THUMB_HOVER_MAX_WIDTH,
  ROW_THUMB_HOVER_MIN_WIDTH,
  ROW_THUMB_HOVER_SCALE,
  ROW_THUMB_WIDTH,
  ROW_THUMB_WIDTH_MOBILE,
} from './mediaConstants';

// Clear of the list's scroll box, but under MUI's modal layer (1300) so the
// quick preview still covers it.
const OVERLAY_Z_INDEX = 1200;
// Keeps the enlarged tile off the very edge of the window.
const VIEWPORT_MARGIN = 8;

const playBadgeSx = {
  color: '#FFFFFF',
  filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.7))',
  fontSize: { xs: '14px', md: '18px', lg: '22px', xl: '30px' },
  [MOBILE]: { fontSize: '20px' },
};

/**
 * The preview cell at the head of a media file's row.
 *
 * It carries no tooltip of its own - the file name, its type and its size are
 * all spelled out in the cell immediately to the right, so a tooltip here would
 * only repeat them under the pointer.
 *
 * It is a real button, so the thumbnail opens the quick preview on click or
 * Enter just as the file name beside it does. Resting the pointer on it swells
 * an enlarged copy of the tile out of the row and, for a video, plays the clip
 * inside it.
 *
 * That enlarged copy is rendered into document.body, not into the row. The list
 * lives in a fixed-height scroll box, and a scroll box clips whatever overflows
 * it: grown in place, the tile was cut off against the top, bottom and left
 * edges of the visible area. A fixed-position portal sits outside that box, so
 * nothing can clip it, and it is clamped to the window so it cannot run off
 * screen either. It takes no pointer events, which leaves the row underneath
 * holding every one of them - so the hover state stays a property of the row and
 * never flickers against the thing the hover produced.
 */
const MediaRowThumbnail = (props) => {
  const { file, canPreview = false, onPreview } = props;

  // A caller that knows the type from the API passes it; otherwise it is read
  // from the extension, which only recognises the common formats.
  const fileType = props.fileType || getFileType(file?.name || '');
  const hasUrl = file?.video_url != null && file?.video_url !== '';
  const previewable = canPreview && hasUrl;
  // The server gave up processing the file, so no URL is ever coming - say so
  // rather than leaving a tile that looks like it is still on its way.
  const processingFailed = !hasUrl && !!file?.processing_error;

  const wrapperRef = useRef(null);
  const [anchorRect, setAnchorRect] = useState(null);
  const [hovering, hoverHandlers] = useHoverIntent(400, previewable);

  useEffect(() => {
    if (!hovering) {
      setAnchorRect(null);
      return undefined;
    }

    const node = wrapperRef.current;
    if (!node || typeof node.getBoundingClientRect !== 'function') return undefined;
    setAnchorRect(node.getBoundingClientRect());

    // The row slides out from under a fixed-position overlay the moment
    // anything scrolls, so drop it rather than leave it floating.
    const dismiss = () => setAnchorRect(null);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('resize', dismiss);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('resize', dismiss);
    };
  }, [hovering]);

  const geometry = useMemo(() => {
    if (!anchorRect || typeof window === 'undefined') return null;

    // Keep the tile's own shape, and keep the result inside the window however
    // small that window is.
    const aspect = anchorRect.width ? anchorRect.height / anchorRect.width : 9 / 16;
    const roomForWidth = Math.max(0, window.innerWidth - VIEWPORT_MARGIN * 2);
    const width = anchorRect.width
      ? Math.min(
          Math.max(anchorRect.width * ROW_THUMB_HOVER_SCALE, ROW_THUMB_HOVER_MIN_WIDTH),
          ROW_THUMB_HOVER_MAX_WIDTH,
          roomForWidth
        )
      : 0;
    const height = width * aspect;
    const furthestLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
    const furthestTop = Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN);

    return {
      width,
      height,
      // Grows around the tile's own centre, then slides back inside the window
      // if that would push it past an edge.
      left: Math.min(Math.max(anchorRect.left + anchorRect.width / 2 - width / 2, VIEWPORT_MARGIN), furthestLeft),
      top: Math.min(Math.max(anchorRect.top + anchorRect.height / 2 - height / 2, VIEWPORT_MARGIN), furthestTop),
    };
  }, [anchorRect]);

  // Mounted at the tile's size and grown to full on the next frame, so the copy
  // appears to swell out of the row rather than snapping into place.
  const [grown, setGrown] = useState(false);

  useEffect(() => {
    if (!geometry) {
      setGrown(false);
      return undefined;
    }
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      setGrown(true);
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => setGrown(true));
    return () => window.cancelAnimationFrame(frame);
  }, [geometry]);

  const showBadge = previewable && fileType === 'video';

  return (
    <Box
      ref={wrapperRef}
      onMouseEnter={hoverHandlers.onMouseEnter}
      onMouseLeave={hoverHandlers.onMouseLeave}
      sx={{ width: ROW_THUMB_WIDTH, flexShrink: 0, [MOBILE]: { width: ROW_THUMB_WIDTH_MOBILE } }}
      data-testid="media-row-thumbnail"
      data-hovering={hovering ? 'true' : 'false'}
    >
      <ButtonBase
        // Dismissing the enlarged copy here rather than waiting for a blur is
        // what keeps a keyboard-opened preview from leaving it behind: the
        // dialog takes focus through a focus trap, and the button is left
        // holding a hover state no DOM event ever clears. Opening the preview is
        // also the moment the copy has nothing left to say - the file is about
        // to be shown at full size.
        onClick={() => {
          if (!previewable) return;
          hoverHandlers.stop();
          if (onPreview) onPreview(file);
        }}
        // Only a keyboard focus raises the enlarged copy. Closing the quick
        // preview hands focus back here programmatically, and onFocusVisible
        // does not fire for that - which is what keeps the tile from being left
        // enlarged once the dialog closes.
        onFocusVisible={hoverHandlers.onFocus}
        onBlur={hoverHandlers.onBlur}
        disabled={!previewable}
        aria-label={hasUrl ? `Preview ${file?.name}` : processingFailed ? `${file?.name} - processing failed` : `${file?.name} - preview not available`}
        focusRipple
        sx={{
          display: 'block',
          width: '100%',
          position: 'relative',
          borderRadius: '4px',
          cursor: previewable ? 'pointer' : 'default',
          '&.Mui-focusVisible': { outline: '2px solid #08231B', outlineOffset: '1px' },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <MediaThumbnail
            file={file}
            fileType={fileType}
            cacheKey={file?.id ? `media-${file.id}` : file?.name}
            radius="4px"
          />
          {processingFailed && (
            <Box
              aria-hidden="true"
              data-testid="media-row-processing-failed"
              sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', borderRadius: '4px', backgroundColor: 'rgba(206, 1, 1, 0.35)' }}
            >
              <ErrorOutlineRoundedIcon sx={playBadgeSx} />
            </Box>
          )}
          {showBadge && (
            <Box
              aria-hidden="true"
              sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}
            >
              <PlayArrowRoundedIcon sx={playBadgeSx} />
            </Box>
          )}
        </Box>
      </ButtonBase>

      {geometry &&
        typeof document !== 'undefined' &&
        createPortal(
          <Box
            aria-hidden="true"
            data-testid="row-thumbnail-overlay"
            sx={{
              position: 'fixed',
              left: `${geometry.left}px`,
              top: `${geometry.top}px`,
              width: `${geometry.width}px`,
              height: `${geometry.height}px`,
              zIndex: OVERLAY_Z_INDEX,
              pointerEvents: 'none',
              borderRadius: '6px',
              overflow: 'hidden',
              backgroundColor: '#0d1a16',
              boxShadow: '0px 10px 34px rgba(8, 35, 27, 0.45)',
              transformOrigin: 'center center',
              transform: grown ? 'scale(1)' : `scale(${1 / ROW_THUMB_HOVER_SCALE})`,
              opacity: grown ? 1 : 0,
              transition: 'transform 220ms cubic-bezier(0.2, 0, 0.2, 1), opacity 160ms ease',
              '@media (prefers-reduced-motion: reduce)': {
                transform: 'none',
                opacity: 1,
                transition: 'none',
              },
            }}
          >
            <MediaThumbnail
              file={file}
              fileType={fileType}
              cacheKey={file?.id ? `media-${file.id}` : file?.name}
              playing={hovering}
              lazy={false}
              radius="6px"
            />
          </Box>,
          document.body
        )}
    </Box>
  );
};

export default memo(MediaRowThumbnail);
